import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import * as React from "react";
import { useSelector } from "react-redux";
import { useFilters, usePagination, useTable } from "react-table";
import translate from "../../../../i18n/translate";
import "./reactTable.scss";

const ReactTable = ({ setInfoDetails, selectedFilters }: any) => {
  const reduxState = useSelector((state: any) => state);
  const [topicLevelWords, setTopicLevelWords] = React.useState<any>([]);
  const [tableBodyContents, setTableBodyContents] = React.useState<any>([]);
  const [showNoWordsMessage, setShowNoWordsMessage] = React.useState<boolean>(false);
  const [selectedHeaderLabels, setSelectedHeaderLabels] = React.useState<any>([]);
  let isPlaying = false;

  React.useEffect(() => {
    if (
      reduxState.vocabularyContents &&
      reduxState.vocabularyContents.topicLevelContent &&
      Object.keys(reduxState.vocabularyContents.topicLevelContent).length > 0
    ) {
      setTopicLevelWords(reduxState.vocabularyContents.topicLevelContent.wordlist);
    } else {
      setTopicLevelWords([]);
    }
  }, [reduxState]);

  React.useEffect(() => {
    if (topicLevelWords && topicLevelWords.length > 0) {
      setShowNoWordsMessage(false);
      const framedState: any = [];
      topicLevelWords.forEach((wordDetails: any) => {
        if (wordDetails) {
          framedState.push({
            word: wordDetails.word.replace(/<\/?[^>]+(>|$)/g, ""),
            translation:
              wordDetails.translation === null || wordDetails.translation === ""
                ? ""
                : wordDetails.translation[0]
                ? wordDetails.translation[0].word.replace(/<\/?[^>]+(>|$)/g, "")
                : "",
            pos: wordDetails.pos.replace(/<\/?[^>]+(>|$)/g, ""),
            definition: wordDetails.definition.replace(/<\/?[^>]+(>|$)/g, ""),
            sentence: wordDetails.sentence.replace(/<\/?[^>]+(>|$)/g, ""),

            audioPath: wordDetails.audioPath,
            sentenceAudio: wordDetails.sentenceAudio,
            notes: wordDetails.notes,
            noteId: wordDetails.noteId,
            defId: wordDetails.defId,
            listId: wordDetails.listId,
            posId: wordDetails.posId,
            wordId: wordDetails.wordId,
            wordlist: wordDetails.wordlist,
            isDeletable: isDeletable(wordDetails),
          });
        }
      });
      setTableBodyContents(framedState);
    } else {
      setShowNoWordsMessage(true);
      setTableBodyContents([]);
    }
  }, [topicLevelWords]);

  React.useEffect(() => {
    if (selectedFilters && typeof selectedFilters === "object") {
      const selectedHeaders = selectedFilters.map((filter: any) => simplifyLabels(filter.name));
      setSelectedHeaderLabels(selectedHeaders);
    }
  }, [selectedFilters]);

  const isDeletable = (wordDetails: any) => {
    const usersWordList = reduxState.vocabularyContents.headerLevelContents.User["userWordlists"];
    if (usersWordList && usersWordList.length > 0) {
      const isAvailableInUsersList = usersWordList.findIndex((userList: any) => userList.listId === wordDetails.listId);
      return isAvailableInUsersList !== -1;
    }
  };

  const data = React.useMemo(() => tableBodyContents, [tableBodyContents]);

  const columns: any = React.useMemo(
    () => [
      {
        Header: translate("vocab_center_word_label"),
        accessor: "word",
        Cell: (props: any) => (
          <div className="audio-container">
            {props.cell.row.original.audioPath !== "" && (
              <VolumeUpIcon
                className="audio-icon"
                onClick={() => {
                  const audioTag = document.createElement("audio");
                  audioTag.src = `${process.env.REACT_APP_PROGRESS_URL}/content/${changeFormat(
                    props.cell.row.original.audioPath
                  )}`;
                  if (!isPlaying) {
                    audioTag.play();
                  }
                  audioTag.addEventListener("play", () => {
                    isPlaying = true;
                  });
                  audioTag.addEventListener("ended", () => {
                    isPlaying = false;
                  });
                }}
              />
            )}
            <p className="audio-icon-label">{props.cell.value}</p>
          </div>
        ),
      },
      {
        Header: translate("vocab_center_translation_label"),
        accessor: "translation",
      },
      {
        Header: translate("vocab_center_pos_label"),
        accessor: "pos",
      },
      {
        Header: translate("vocab_center_definition_label"),
        accessor: "definition",
      },
      {
        Header: translate("vocab_center_sentence_label"),
        accessor: "sentence",
        Cell: (props: any) => (
          <div className="audio-container">
            {props.cell.row.original.sentenceAudio !== "" && (
              <VolumeUpIcon
                className="audio-icon"
                onClick={() => {
                  const audioTag = document.createElement("audio");
                  audioTag.src = `${process.env.REACT_APP_PROGRESS_URL}/content/${changeFormat(
                    props.cell.row.original.sentenceAudio
                  )}`;
                  if (!isPlaying) {
                    audioTag.play();
                  }
                  audioTag.addEventListener("play", () => {
                    isPlaying = true;
                  });
                  audioTag.addEventListener("ended", () => {
                    isPlaying = false;
                  });
                }}
              />
            )}
            <p className="audio-icon-label">{props.cell.value}</p>
          </div>
        ),
      },
    ],
    [selectedHeaderLabels]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      initialState: {
        hiddenColumns: selectedHeaderLabels,
      },
    }
  );

  const changeFormat = (url: any) => {
    return url.replace(".wav", ".mp3");
  };

  const simplifyLabels = (label: any) => {
    switch (label) {
      case "Word":
        return "word";
      case "Translation":
        return "translation";
      case "Parts of Speech":
        return "pos";
      case "Definition":
        return "definition";
      case "Sentence":
        return "sentence";
    }
  };

  return showNoWordsMessage ? (
    <div className="no-data-table-body-contents">
      {!reduxState.vocabularyContents.isLoading && !reduxState.vocabularyContents.isSubTopicLoading && (
        <p className="no-data-message">"{translate("vocab_center_no_word_message")}"</p>
      )}
    </div>
  ) : !showNoWordsMessage ? (
    <table {...getTableProps()} className="react-table-container">
      <thead className="react-table-header-container">
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()} className="react-table-header-row">
            {headerGroup.headers.map((column) => (
              <th {...column.getHeaderProps()} className="react-table-header-cell">
                {column.render("Header")}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()} className="react-table-body-container">
        {rows.map((row) => {
          prepareRow(row);
          return (
            <tr
              {...row.getRowProps()}
              className="react-table-body-row"
              onDoubleClick={() => {
                setInfoDetails(row.original);
              }}
            >
              {row.cells.map((cell) => {
                return (
                  <td {...cell.getCellProps()} className="react-table-row-cell">
                    {cell.render("Cell")}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  ) : (
    <> </>
  );
};

export default ReactTable;
